import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Chart from '../components/Chart';
import MetricCard from '../components/MetricCard';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiStar, FiClock, FiTrendingUp, FiAward, FiTarget } = FiIcons;

const Performance = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedTeam, setSelectedTeam] = useState('all');

  const performanceMetrics = [
    {
      title: 'Team Performance',
      value: '4.3/5',
      change: '0.2',
      changeType: 'positive',
      icon: FiStar,
      color: 'success',
      subtitle: 'Average rating'
    },
    {
      title: 'Response Time',
      value: '2.4h',
      change: '0.6h',
      changeType: 'positive',
      icon: FiClock,
      color: 'primary',
      subtitle: 'Average first response'
    },
    {
      title: 'Resolution Rate',
      value: '94%',
      change: '3%',
      changeType: 'positive',
      icon: FiTarget,
      color: 'warning',
      subtitle: 'First contact resolution'
    },
    {
      title: 'Customer Mentions',
      value: '156',
      change: '23',
      changeType: 'positive',
      icon: FiUsers,
      color: 'secondary',
      subtitle: 'Staff mentioned by name'
    }
  ];

  const teamPerformanceData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    series: [
      {
        name: 'Customer Success',
        type: 'line',
        data: [4.1, 4.2, 4.3, 4.2, 4.4, 4.3],
        smooth: true,
        lineStyle: { color: '#0ea5e9' }
      },
      {
        name: 'Technical Support',
        type: 'line',
        data: [3.8, 3.9, 4.0, 4.1, 4.2, 4.1],
        smooth: true,
        lineStyle: { color: '#22c55e' }
      },
      {
        name: 'Sales',
        type: 'line',
        data: [4.0, 4.1, 4.0, 4.2, 4.3, 4.4],
        smooth: true,
        lineStyle: { color: '#f59e0b' }
      },
      {
        name: 'Account Management',
        type: 'line',
        data: [4.2, 4.3, 4.4, 4.3, 4.5, 4.4],
        smooth: true,
        lineStyle: { color: '#8b5cf6' }
      }
    ]
  };

  const responseTimeData = {
    labels: ['Customer Success', 'Technical Support', 'Sales', 'Account Management', 'Billing'],
    series: [
      {
        name: 'Current Month',
        type: 'bar',
        data: [2.1, 3.2, 1.8, 2.5, 4.1],
        itemStyle: { color: '#0ea5e9' }
      },
      {
        name: 'Previous Month',
        type: 'bar',
        data: [2.8, 3.9, 2.2, 3.1, 4.8],
        itemStyle: { color: '#94a3b8' }
      }
    ]
  };

  const topPerformers = [
    {
      name: 'Sarah Johnson',
      role: 'Customer Success Manager',
      avatar: 'SJ',
      rating: 4.8,
      mentions: 24,
      responseTime: '1.2h',
      resolutionRate: '98%',
      feedback: [
        'Extremely helpful and knowledgeable',
        'Went above and beyond to solve my issue',
        'Very professional and friendly'
      ]
    },
    {
      name: 'Mike Chen',
      role: 'Technical Support Lead',
      avatar: 'MC',
      rating: 4.6,
      mentions: 18,
      responseTime: '2.1h',
      resolutionRate: '95%',
      feedback: [
        'Great technical expertise',
        'Explained complex issues clearly',
        'Quick to identify the problem'
      ]
    },
    {
      name: 'Emily Rodriguez',
      role: 'Account Manager',
      avatar: 'ER',
      rating: 4.7,
      mentions: 21,
      responseTime: '1.8h',
      resolutionRate: '96%',
      feedback: [
        'Always available when needed',
        'Proactive in addressing concerns',
        'Excellent communication skills'
      ]
    }
  ];

  const improvementOpportunities = [
    {
      team: 'Technical Support',
      issue: 'Response Time',
      current: '3.2h',
      target: '2.5h',
      recommendation: 'Implement ticket prioritization system',
      impact: 'High'
    },
    {
      team: 'Billing',
      issue: 'Customer Satisfaction',
      current: '3.8/5',
      target: '4.2/5',
      recommendation: 'Additional training on billing processes',
      impact: 'Medium'
    },
    {
      team: 'Sales',
      issue: 'Follow-up Consistency',
      current: '78%',
      target: '90%',
      recommendation: 'Automated follow-up reminders',
      impact: 'Medium'
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Team Performance</h1>
          <p className="text-secondary-600 mt-1">Track and improve team performance based on customer feedback</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select 
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Teams</option>
            <option value="customer-success">Customer Success</option>
            <option value="technical">Technical Support</option>
            <option value="sales">Sales</option>
            <option value="account-mgmt">Account Management</option>
          </select>
          
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            Generate Report
          </motion.button>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {performanceMetrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart 
          title="Team Performance Trends"
          data={teamPerformanceData}
          type="line"
          height={400}
        />
        
        <Chart 
          title="Response Time Comparison (Hours)"
          data={responseTimeData}
          type="bar"
          height={400}
        />
      </div>

      {/* Top Performers */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-900">Top Performers</h3>
          <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            View All Team Members
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {topPerformers.map((performer, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{performer.avatar}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-secondary-900">{performer.name}</h4>
                  <p className="text-sm text-secondary-600">{performer.role}</p>
                </div>
                <div className="ml-auto">
                  <div className="flex items-center space-x-1">
                    <FiStar className="w-4 h-4 text-warning-500" />
                    <span className="font-semibold text-secondary-900">{performer.rating}</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-secondary-900">{performer.mentions}</p>
                  <p className="text-xs text-secondary-500">Mentions</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-secondary-900">{performer.responseTime}</p>
                  <p className="text-xs text-secondary-500">Avg Response</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-secondary-900">{performer.resolutionRate}</p>
                  <p className="text-xs text-secondary-500">Resolution</p>
                </div>
              </div>
              
              <div>
                <h5 className="text-sm font-medium text-secondary-900 mb-2">Recent Feedback:</h5>
                <div className="space-y-1">
                  {performer.feedback.slice(0, 2).map((feedback, feedbackIndex) => (
                    <p key={feedbackIndex} className="text-xs text-secondary-600 italic">
                      "{feedback}"
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Improvement Opportunities */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Improvement Opportunities</h3>
        
        <div className="space-y-4">
          {improvementOpportunities.map((opportunity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-secondary-900">{opportunity.team}</h4>
                  <p className="text-sm text-secondary-600">{opportunity.issue}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  opportunity.impact === 'High' 
                    ? 'bg-danger-100 text-danger-700' 
                    : 'bg-warning-100 text-warning-700'
                }`}>
                  {opportunity.impact} Impact
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-xs text-secondary-500">Current</p>
                  <p className="text-sm font-medium text-secondary-900">{opportunity.current}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary-500">Target</p>
                  <p className="text-sm font-medium text-success-600">{opportunity.target}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-sm text-secondary-700">{opportunity.recommendation}</p>
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 bg-primary-50 text-primary-600 text-sm rounded-lg hover:bg-primary-100 transition-colors">
                    Create Action Plan
                  </button>
                  <button className="px-3 py-1 bg-secondary-50 text-secondary-600 text-sm rounded-lg hover:bg-secondary-100 transition-colors">
                    Schedule Training
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Performance;